import React, { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useFocusEffect } from '@react-navigation/native';
import { RefreshControl, ScrollView, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import HtmlRenderer from 'react-native-render-html';
import Toast from 'react-native-root-toast';

import { STATUS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import { getLocalTime } from '../../../common/utils/time/time';
import { EmptyState, ErrorCallout, Heading, Spinner, Text } from '../../components';
import { GET_LATEST_NOTIFICATIONS, NOTIFICATION_TOGGLE_READ } from '../../graphql/notification';
import { useRefreshPage } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const ChecklistNotificationsView = ({ navigation }) => {
	const styles = useThemedStyles(createStyles);
	const { tenant } = useUserContext();
	const { width } = useWindowDimensions();
	const { data, loading, error, refetch } = useQuery(GET_LATEST_NOTIFICATIONS, {
		fetchPolicy: 'cache-and-network',
	});
	const [markRead] = useMutation(NOTIFICATION_TOGGLE_READ);
	const refresh = useCallback(() => refetch().catch(() => {}), [refetch]);
	const [refreshPage, isRefreshing] = useRefreshPage(refresh);

	useFocusEffect(useCallback(() => {
		refresh();
	}, [refresh]));

	const notifications = data?.getLatestNotifications || [];
	const openNotification = notification => {
		if (notification.status !== STATUS.READ) {
			markRead({ variables: { id: notification.id } }).catch(() => {
				Toast.show('Unable to mark the notification as read');
			});
		}
		navigation.navigate('ChecklistSignOffView', { checklistSubmissionId: notification.checklistSubmissionId });
	};

	if (loading && !data) {
		return <Spinner />;
	}

	return (
		<ScrollView
			style={styles.container}
			contentContainerStyle={styles.content}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
		>
			<Heading heading="Notifications" />
			{Boolean(error) && <ErrorCallout text="Notifications could not be refreshed. Pull down to try again." />}
			{
				notifications.length === 0 && !error && (
					<EmptyState icon="notifications-outline" message="No notifications yet" />
				)
			}
			{
				notifications.map(notification => (
					<TouchableOpacity
						key={notification.id}
						style={styles.card}
						disabled={!notification.checklistSubmissionId}
						accessibilityRole={notification.checklistSubmissionId ? 'button' : undefined}
						onPress={() => openNotification(notification)}
					>
						<View>
							<HtmlRenderer
								html={notification.content || ''}
								imagesMaxWidth={Math.max(1, width - 48)}
								baseFontStyle={styles.notificationText}
							/>
							<Text style={styles.date}>
								{getLocalTime(tenant?.timezone, notification.created).format('ddd D MMM, h:mm a')}
							</Text>
							{
								Boolean(notification.checklistSubmissionId) && (
									<Text style={styles.action}>Open checklist</Text>
								)
							}
						</View>
					</TouchableOpacity>
				))
			}
		</ScrollView>
	);
};

const createStyles = theme => ({
	container: {
		flex: 1,
		backgroundColor: theme.mainBg4,
	},
	content: {
		padding: 10,
		paddingBottom: 30,
	},
	card: {
		backgroundColor: theme.mainBg1,
		borderRadius: 12,
		borderWidth: 1,
		borderColor: theme.border3,
		padding: 12,
		marginBottom: 10,
	},
	notificationText: {
		fontSize: 16,
		color: theme.text1,
	},
	date: {
		fontSize: 13,
		color: theme.text3,
		marginTop: 8,
	},
	action: {
		fontSize: 14,
		fontWeight: '600',
		color: theme.text1,
		marginTop: 8,
	},
});

export default ChecklistNotificationsView;
